<template>
  <ModalDialog
      :open="open"
      :button-full-w="true"
      :no-padding="true"
      :vertical-scroll="false"
      @close="close">
    <template #body>
      <div class="px-6 overflow-y-auto overflow-x-hidden">
        <div class="mt-2 pb-6 flex flex-col gap-2 w-full items-center">
          <ul class="grid gap-2 w-full">
            <LoadingView v-if="isLoading && attachments.length > 0" :absolute="true" />
            <li class="w-full overflow-x-auto" v-if="attachments.length > 0 && !isLoading">
              <div class="overflow-y-visible pb-4 px-2">
                <ul class="flex gap-3 items-center min-w-max">
                  <li v-for="(item, index) in attachments" :key="`preview-${index}`" class="relative rounded-xl group shrink-0" @click="updateSelection(item, $event)" :class="{'border-4 border-bhav-blue-500': isSelected(item)}">
                    <img :src="getImageURL(item)" :alt="item.file?.name || item.filename"
                         class="w-32 h-32 object-cover rounded-lg">
                    <div v-if="validationMap[index]" class="flex gap-4 absolute top-1 w-6 h-6 left-1 z-10 items-center justify-center rounded text-white bg-bhav-green-600">
                      <span class="fak fa-check w-4 h-4" />
                    </div>
                    <div v-if="overLimit(item)" class="flex absolute top-1 w-6 h-6 text-2xl left-1 z-10 items-center justify-center rounded text-bhav-red-100" title="Image bigger than 2MB">
                      <span class="fak fa-alert" />
                    </div>
                    <button @click.stop="removeItem(index)"
                            class="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-white w-6 h-6 rounded-md flex items-center text-xs justify-center text-bhav-blue-200 hover:text-bhav-blue-600 hover:bg-bhav-cool-gray-100">
                      <span class="fa fa-close"></span>
                    </button>
                  </li>
                  <li
                      @drop.prevent="drop"
                      @dragenter="dragEnter"
                      @dragleave="dragLeave"
                      @dragover="dragOver"
                      class="group min-w-72 h-32 transition-all duration-300 border-[1.25px] border-dashed rounded-lg flex flex-col items-center justify-center text-center"
                      :class="isDragging ? 'text-bhav-blue-300 border-bhav-blue-300' : 'text-bhav-blue-200 border-bhav-blue-200 hover:text-bhav-blue-300 hover:border-bhav-blue-300'">
                    <span class="fak fa-photo text-5xl transition-all duration-300 py-4" :class="isDragging ? '-rotate-12' : 'group-hover:-rotate-12'"></span>
                    <p class="text-sm">Drag & drop your files or <label for="upload" class="cursor-pointer underline text-bhav-blue-500 hover:text-bhav-blue-600">choose files</label></p>
                    <input id="upload" @change="(e) => { upload(e.target.files); }" type="file" multiple="true" accept="image/png,image/jpeg,image/webp,image/svg+xml" class="sr-only">
                  </li>
                </ul>
              </div>
            </li>
            <li class="col-span-full" v-if="attachments.length > 0">
              <div class="w-full rounded-lg p-2 bg-bhav-cool-gray-50">
                <div class="flex gap-1 w-full items-center">
                  <span class="fak fa-pen text-xl text-bhav-blue-200" v-show="!isTitleFocused && !itemShowingInformation.alt"></span>
                  <input class="w-full truncate border-0 bg-transparent text-2xl text-bhav-blue-500 placeholder-bhav-blue-200"
                         v-model="itemShowingInformation.alt"
                         @focus="this.updateTitleFocus(true)"
                         @blur="this.updateTitleFocus(false)"
                         placeholder="Public title"/>
                </div>
                <div class="flex gap-0.5 w-full items-center">
                  <span class="fak fa-pen text-base text-bhav-blue-200" v-show="!isDescriptionFocused && !itemShowingInformation.caption"></span>
                  <input class="w-full truncate border-0 bg-transparent text-bhav-blue-300 placeholder-bhav-blue-200"
                         v-model="itemShowingInformation.caption"
                         @focus="this.updateDescriptionFocus(true)"
                         @blur="this.updateDescriptionFocus(false)"
                         placeholder="Public description"/>
                </div>
              </div>
              <div class="w-full pt-3 rounded-lg gap-1">
                <div class=" text-sm font-normal text-bhav-blue-200">
                  {{ selected.length > 1 ? selected.length + ' images selected.' : selected[0].filename ?? selected[0].file.name }}
                </div>
              </div>
              <div class="w-full my-4 bg-bhav-blue-50 h-[1px] self-stretch">
              </div>
              <div class="flex gap-4 items-start">
                <div class="flex p-2 rounded-lg items-center bg-bhav-cool-gray-50 gap-4">
                  <div>
                    <span class="fak fa-list-view text-base text-bhav-blue-500 mb-1"></span>
                    <MultiselectField label="Select Category" @update:model-value="(newValue) => { if(newValue !== 'home') {itemShowingInformation.meta.position = null} }"
                                      :show-search="false"
                                      :show-create="false"
                                      v-model="itemShowingInformation.meta.category"
                                      color="orange"
                                      :options="this.categories"
                                      instructions="Select a Category"
                                      dropdownDirection="up"
                                      max-dropdown-height="24vh"/>
                  </div>
                  <span class="fak fa-swap-horizontal text-bhav-blue-200 text-base"></span>
                  <div>
                                        <span class="fak fa-shown text-base mb-1"
                                              :class="itemShowingInformation?.meta?.category != 'home' ? 'text-bhav-blue-200' : 'text-bhav-blue-500'"></span>
                    <MultiselectField label="Select View"
                                      :show-search="false"
                                      :show-create="false"
                                      :disabled="itemShowingInformation?.meta?.category != 'home'"
                                      v-model="itemShowingInformation.meta.position"
                                      color="orange"
                                      :options="this.positions.map(position => ({ label: position, value: position}))"
                                      instructions="Select a View"
                                      dropdownDirection="up"
                                      max-dropdown-height="24vh"/>
                  </div>
                </div>
                <div class="h-auto bg-bhav-blue-50 w-[1px] self-stretch">
                </div>
                <div class="p-2 flex-1 min-w-0">
                  <span class="fak fa-password text-base text-bhav-blue-500 mb-1"></span>
                  <MultiselectField label="Add Keywords"
                                    :show-create="true"
                                    :multiple="true"
                                    @create="(e) => {storeKeyword(e)}"
                                    v-model="itemShowingInformation.keywords"
                                    color="orange"
                                    :options="this.keywords.map(keyword => ({ label: keyword.name, value: keyword.id}))" instructions="Select the keywords or create one"
                                    dropdownDirection="up"
                                    max-dropdown-height="272px"/>
                </div>
              </div>
              <div class="w-full pt-3 rounded-lg gap-1">
                <div class=" text-sm font-semibold text-bhav-blue-500">Tags Information</div>
                <div class=" text-sm font-normal text-bhav-blue-200">Tagging an image is now required. This will allow for the use of filters and quick searches within the media library.</div>
              </div>
              <div class="w-full my-4 bg-bhav-blue-50 h-[1px] self-stretch">
              </div>
              <div class="flex flex-wrap" id="test">
                <div class="p-2">
                  <span class="fak fa-community text-base mb-1 text-bhav-blue-500"></span>
                  <div>
                    <MultiselectField label="Add Community"
                                      v-model="singleCommunity"
                                      :show-search="false"
                                      :show-create="false"
                                      :multiple="false"
                                      color="blue"
                                      @update:model-value="() => { singleCollection = null; itemShowingInformation.meta.floorplan = null; }"
                                      :options="communities.map(community => ({ label: community.meta?.info?.name ?? community.kova?.WebGroupName, value: community.id }))"
                                      instructions="Select a Community"
                                      dropdownDirection="up"
                                      max-dropdown-height="24vh"/>
                  </div>
                </div>
                <div class="p-2">
                  <span class="fak fa-collection text-base min-w-8 mb-1"  :class="singleCommunity == null ? 'text-bhav-blue-200' : 'text-bhav-blue-500'"></span>
                  <div>
                    <MultiselectField label="Add Collection"
                                      v-model="singleCollection"
                                      :show-search="false"
                                      :show-create="false"
                                      :multiple="false"
                                      :disabled="singleCommunity == null"
                                      color="blue"
                                      min-list-width="min-w-96"
                                      @update:model-value="() => { itemShowingInformation.meta.floorplan = null }"
                                      :options="itemShowingInformation.meta.communities != null && itemShowingInformation.meta.communities.length > 0 ? collections.map(collection => ({label: collection.kova?.MarketingName, value: collection.community_rid})) : []"
                                      instructions="Select a Collection"
                                      dropdownDirection="up"
                                      max-dropdown-height="24vh"/>
                  </div>
                </div>
                <div class="p-2">
                  <span class="fak fa-floorplan text-base mb-1"  :class="singleCollection == null ? 'text-bhav-blue-200' : 'text-bhav-blue-500'"></span>
                  <MultiselectField label="Add Floorplan"
                                    v-model="itemShowingInformation.meta.floorplan"
                                    :show-search="false"
                                    :show-create="false"
                                    :disabled="singleCollection == null"
                                    :multiple="false"
                                    color="blue"
                                    :options="itemShowingInformation.meta.collections != null && itemShowingInformation.meta.collections.length > 0 ? filtered.map(item => ({ label: item.name, value: item.id })) : []"
                                    instructions="Select a Floorplan"
                                    dropdownDirection="up"
                                    max-dropdown-height="24vh"/>
                </div>
                <!-- <div class="p-2">
                    <span class="fa fa-garage text-base mb-1 text-bhav-blue-500"></span>
                    <MultiselectField label="Add Elevation"
                        v-model="itemShowingInformation.meta.elevation"
                        :show-search="false"
                        :show-create="false"
                        :multiple="false"
                        color="blue"
                        :options="[]"
                        instructions="Select an Elevation"/>
                </div>
                <div class="p-2">
                    <span class="fak fa-color-package text-base mb-1 text-bhav-blue-500"></span>
                    <MultiselectField label="Add Color Package"
                        v-model="itemShowingInformation.meta.color_package"
                        :show-search="false"
                        :show-create="false"
                        :multiple="false"
                        color="blue"
                        :options="[]"
                        instructions="Select a Color Package"/>
                </div> -->
              </div>
            </li>
            <li v-if="attachments.length == 0"
                @drop.prevent="drop"
                @dragenter="dragEnter"
                @dragleave="dragLeave"
                @dragover="dragOver"
                class="group p-8 h-80 mb-6 transition-all duration-300 border-[1.25px] border-dashed rounded-lg w-full flex flex-col items-center justify-center text-center"
                :class="isDragging ? 'text-bhav-blue-300 border-bhav-blue-300' : 'text-bhav-blue-200 border-bhav-blue-200 hover:text-bhav-blue-300 hover:border-bhav-blue-300'">
              <span class="fak fa-photo text-5xl transition-all duration-300 p-4" :class="isDragging ? '-rotate-12' : 'group-hover:-rotate-12'"></span>
              <p class="text-sm">Drag & drop your files or <label for="upload" class="cursor-pointer underline text-bhav-blue-500 hover:text-bhav-blue-600">choose files</label></p>
              <p class="text-[12px]">PNG, JPG, WEBP or SVG, {{single}}MB max file size.</p>
              <input id="upload" @change="(e) => { upload(e.target.files); }" type="file" multiple="true" accept="image/png,image/jpeg,image/webp,image/svg+xml" class="sr-only">
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #buttons v-if="attachments.length > 0">
      <div class="w-full flex justify-between px-2">
        <div class="flex gap-2">
          <FormButton @click="applySelectionFull" v-if="attachments.length > 1" :disabled="applyToAllDisabled" color="gray">{{ this.selected.length > 1 ? 'Apply to Selected' : 'Apply to All' }}</FormButton>
          <FormButton v-if="isEditMode && attachments.length > 1" @click="applySelection" :disabled="applyUpdatesDisabled" color="gray">Apply Updates</FormButton>
        </div>
        <div>
          <FormButton @click="close" color="gray" class="mr-2">Cancel</FormButton>
          <FormButton @click="saveAll()" :disabled="!valid" font-style="font-normal" color="blue" class="pl-3 pr-3">{{ this.attachments.find(item => item.id ?? false) ? 'Confirm Update' : 'Confirm Upload' }}</FormButton>
        </div>
      </div>
    </template>
  </ModalDialog>


  <ModalDialog v-if="applied" :b :hide-header="true" @close="() => { applied = false; }" :no-padding="true" :vertical-scroll="false" :open="!!applied">
    <template #body>
      <div class="w-full h-32 rounded-lg bg-bhav-blue-100 flex items-center justify-center mb-3"><span class="text-bhav-blue-500 text-5xl fak fa-blog"></span></div>
      <div class="text-lg flex w-full items-center justify-center font-medium text-bhav-blue-500 mb-1">{{ applied === 'all' || applied === 'selected' ? 'Data' : 'Updates' }} {{ applied === 'selected' || applied === 'update_selected' ? 'applied to selected images.' : 'applied to all images.' }}</div>
    </template>
    <template #buttons>
      <FormButton @click="() => { applied = false; }" color="gray" class="w-full items-center justify-center">Ok</FormButton>
    </template>
  </ModalDialog>
</template>

<script>
import axios from "axios";
import ModalDialog from "./ModalDialog.vue";
import FormButton from "@/components/form/FormButton.vue";
import Media from '@/models/media';
import LoadingView from "./LoadingView.vue";
import SelectField from "./form/SelectField.vue";
import SelectMultipleField from "./form/SelectMultipleField.vue";
import { KeywordsController } from "@/controllers/keywords";
import CheckboxInput from "./form/CheckboxInput.vue";
import MultiselectField from "./form/MultiselectField.vue";
import Keyword from "@/models/keyword";

export default {
  emits: [
    'save',
    'close'
  ],
  data() {
    return {
      attachments: [],
      floorplans: [],
      communities: [],
      isLoading: true,
      categories: Media.categories,
      positions: Media.positions,
      isTitleFocused: false,
      isDescriptionFocused: false,
      applied: false,
      selected: [],
      keywords: null,
      test: null,
      single: 2, // MB
      isDragging: false,
      dragDepth: 0,
      originalSnapshots: new Map() // Store original values for change detection
    };
  },
  methods: {
    // Deep equality check for comparing values
    deepEqual(obj1, obj2) {
      if (obj1 === obj2) return true;
      if (obj1 == null || obj2 == null) return obj1 === obj2;
      if (typeof obj1 !== typeof obj2) return false;
      if (typeof obj1 !== 'object') return obj1 === obj2;

      // Handle arrays
      if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) return false;
        const sorted1 = [...obj1].sort();
        const sorted2 = [...obj2].sort();
        return sorted1.every((val, idx) => this.deepEqual(val, sorted2[idx]));
      }

      if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

      // Handle objects
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;

      return keys1.every(key => this.deepEqual(obj1[key], obj2[key]));
    },

    // Compute which fields changed by comparing current item with its original snapshot
    computeChangedFields(currentItem) {
      const itemKey = currentItem.id || currentItem.file?.name;
      const original = this.originalSnapshots.get(itemKey);

      if (!original) {
        // No original snapshot means this is a new item, return all fields
        return { ...currentItem };
      }

      const patch = {};

      // Check top-level fields
      if (!this.deepEqual(currentItem.alt, original.alt)) {
        patch.alt = currentItem.alt;
      }
      if (!this.deepEqual(currentItem.caption, original.caption)) {
        patch.caption = currentItem.caption;
      }
      if (!this.deepEqual(currentItem.keywords, original.keywords)) {
        patch.keywords = currentItem.keywords;
      }

      // Check meta fields
      const metaChanged = {};
      if (!this.deepEqual(currentItem.meta?.category, original.meta?.category)) {
        metaChanged.category = currentItem.meta?.category;
      }
      if (!this.deepEqual(currentItem.meta?.position, original.meta?.position)) {
        metaChanged.position = currentItem.meta?.position;
      }
      if (!this.deepEqual(currentItem.meta?.communities, original.meta?.communities)) {
        metaChanged.communities = currentItem.meta?.communities;
      }
      if (!this.deepEqual(currentItem.meta?.collections, original.meta?.collections)) {
        metaChanged.collections = currentItem.meta?.collections;
      }
      if (!this.deepEqual(currentItem.meta?.floorplan, original.meta?.floorplan)) {
        metaChanged.floorplan = currentItem.meta?.floorplan;
      }
      if (!this.deepEqual(currentItem.meta?.elevation, original.meta?.elevation)) {
        metaChanged.elevation = currentItem.meta?.elevation;
      }
      if (!this.deepEqual(currentItem.meta?.color_package, original.meta?.color_package)) {
        metaChanged.color_package = currentItem.meta?.color_package;
      }

      // Only add meta to patch if there are changes
      if (Object.keys(metaChanged).length > 0) {
        patch.meta = metaChanged;
      }

      return patch;
    },

    // Apply patch to a target item, preserving fields not in the patch
    applyPatch(targetItem, patch) {
      // Apply top-level fields
      if ('alt' in patch) targetItem.alt = patch.alt;
      if ('caption' in patch) targetItem.caption = patch.caption;
      if ('keywords' in patch) targetItem.keywords = patch.keywords;

      // Apply meta fields
      if (patch.meta) {
        if (!targetItem.meta) targetItem.meta = {};
        if ('category' in patch.meta) targetItem.meta.category = patch.meta.category;
        if ('position' in patch.meta) targetItem.meta.position = patch.meta.position;
        if ('communities' in patch.meta) targetItem.meta.communities = patch.meta.communities;
        if ('collections' in patch.meta) targetItem.meta.collections = patch.meta.collections;
        if ('floorplan' in patch.meta) targetItem.meta.floorplan = patch.meta.floorplan;
        if ('elevation' in patch.meta) targetItem.meta.elevation = patch.meta.elevation;
        if ('color_package' in patch.meta) targetItem.meta.color_package = patch.meta.color_package;
      }
    },

    // Create and store a deep snapshot of an item
    createSnapshot(item) {
      const itemKey = item.id || item.file?.name;
      if (!this.originalSnapshots.has(itemKey)) {
        this.originalSnapshots.set(itemKey, JSON.parse(JSON.stringify({
          alt: item.alt,
          caption: item.caption,
          keywords: item.keywords,
          meta: {
            category: item.meta?.category,
            position: item.meta?.position,
            communities: item.meta?.communities,
            collections: item.meta?.collections,
            floorplan: item.meta?.floorplan,
            elevation: item.meta?.elevation,
            color_package: item.meta?.color_package
          }
        })));
      }
    },

    updateSelection(item, event) {
      const currentIndex = this.attachments.findIndex(i =>
          i.id ? i.id === item.id : i.file?.name === item.file?.name
      );

      // Cmd/Ctrl + Click: Toggle individual item
      if (event?.metaKey || event?.ctrlKey) {
        const selectedIndex = this.selected.findIndex(i =>
            i.id ? i.id === item.id : i.file?.name === item.file?.name
        );

        if (selectedIndex > -1) {
          // Item is selected, remove it (but keep at least one item selected)
          if (this.selected.length > 1) {
            this.selected.splice(selectedIndex, 1);
          }
        } else {
          // Item is not selected, add it
          this.selected.push(item);
        }
        return;
      }

      // Shift + Click: Range selection
      if (event?.shiftKey && this.selected.length > 0) {
        // Find the last selected item's index in attachments
        const lastSelected = this.selected[this.selected.length - 1];
        const lastIndex = this.attachments.findIndex(i =>
            i.id ? i.id === lastSelected.id : i.file?.name === lastSelected.file?.name
        );

        // Select all items between lastIndex and currentIndex
        const startIndex = Math.min(lastIndex, currentIndex);
        const endIndex = Math.max(lastIndex, currentIndex);

        this.selected = this.attachments.slice(startIndex, endIndex + 1);
        return;
      }

      // Regular click: Select only this item (clear others)
      this.selected = [item];
    },
    storeKeyword(keyword) {
      KeywordsController.store(new Keyword({
        name: keyword
      }))
          .then(resp => {
            this.keywords.push(resp);
            if (!this.itemShowingInformation.keywords) {
              this.itemShowingInformation.keywords = [];
            }
            this.itemShowingInformation.keywords.push(resp.id);
          })
    },
    isSelected(item) {
      return this.selected.some(i => i.id ? i.id === item.id : i.file === item.file);
    },
    indexCommunities() {
      axios
          .get(`divisions/communities`)
          .then((res) => {
            this.communities = res.data
                .map(community => {
                  community.collections = community.collections
                      .filter(collection => collection.status != 'inactive')
                      .sort((a,b) => a.meta.info.name > b.meta.info.name ? 1 : -1);
                  return community;
                })
                .filter(community => community.status != 'inactive' && community.collections.length > 0)
                .sort((a,b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
            this.isLoading = false;
          })
          .catch((e) => {
            throw e;
          });
    },
    index() {
      axios
          .get(`floorplans`)
          .then((res) => {
            this.floorplans = res.data;
            this.isLoading = false;
          })
          .catch((e) => {
            throw e;
          });
    },
    indexKeywords() {
      KeywordsController.index()
          .then(resp => {
            this.keywords = resp;
            this.isLoading = false;
          })
          .catch(error => {
            console.error(error);
            throw error;
          })
    },
    overLimit(item) {
      if(item.file && item.file.size > (this.single * 1024 * 1024)) {
        item.message = "Over image file size limit.";
        return true;
      }
      return false;
    },
    updateTitleFocus(focus) {
      this.isTitleFocused = focus;
    },
    updateDescriptionFocus(focus) {
      this.isDescriptionFocused = focus;
    },
    getImageURL(item) {
      if (item.file) {
        return URL.createObjectURL(item.file);
      } else if (item.url) {
        return item.url;
      }
      return '';
    },
    removeItem(i) {
      const item = this.attachments[i];
      this.attachments.splice(i,1);

      // Remove from selected array if it was selected
      const selectedIndex = this.selected?.findIndex(selected =>
        selected.id ? selected.id === item.id : selected.file === item.file
      );

      if (selectedIndex > -1) {
        this.selected?.splice(selectedIndex, 1);

        // If selection is now empty but there are still attachments, select an item
        if (this.selected.length === 0 && this.attachments.length > 0) {
          // Select the item at the same index, or the previous one if we removed the last item
          const newIndex = Math.min(i, this.attachments.length - 1);
          this.selected = [this.attachments[newIndex]];
        }
      }
    },
    drop(data) {
      this.isDragging = false;
      this.dragDepth = 0;
      if(data.dataTransfer.files.length) {
        this.upload(data.dataTransfer.files);
      }
    },
    dragEnter(e) {
      e.preventDefault();
      this.dragDepth++;
      this.isDragging = true;
    },
    dragLeave(e) {
      e.preventDefault();
      this.dragDepth--;
      if (this.dragDepth === 0) {
        this.isDragging = false;
      }
    },
    dragOver(e) {
      e.preventDefault();
    },
    upload(files) {
      if(files.length) {
        this.selected = [];
        const newItems = Array.from(files).map(file => {
          const item = {
            ...(new Media()),
            // need to remove this.
            keywords: [],
            file,
            type: 'URL'
          };
          // Create snapshot for new uploads
          this.createSnapshot(item);
          return item;
        });
        this.attachments = [
          ...this.attachments,
          ...newItems
        ];
        // include selection on the first item
        if(this.selected.length == 0 && this.attachments.length > 0) {
          this.updateSelection(this.attachments[0])
        }
      }
    },
    async save(i) {
      let item = this.attachments[i];
      item.saving = true;
      const request = item.id == null
          ? axios.postForm(`media`, item)
          :  axios.patch(`media/${item.id}`, item);

      await request
          .then(async resp => {
            this.attachments[i] = new Media(resp.data);
          })
          .catch(error => {
            item.message = error.resp?.data?.message || error.message;
          });
    },
    async saveAll() {
      if(this.attachments.length > 0) {
        this.isLoading = true;
        await Promise.all(
            this.attachments.map((_, i) => this.save(i))
        );
        this.isLoading = false;
        this.$emit('save', this.saved);
        this.close();
      }
    },
    close() {
      this.attachments = [];
      this.$emit('close');
    },
    applySelectionFull() {
      // Apply all fields (full copy) - used for "Apply to All" / "Apply to Selected"
      const applyTo = this.selected.length > 1 ? this.selected : this.attachments;
      const template = this.itemShowingInformation;

      applyTo.forEach(item => {
        const itemKey = item.id || item.file?.name;
        const templateKey = template.id || template.file?.name;

        // Don't apply to the template itself
        if (itemKey !== templateKey) {
          item.alt = template.alt;
          item.caption = template.caption;
          item.meta.category = template.meta?.category;
          item.meta.position = template.meta?.position;
          item.meta.collections = template.meta?.collections;
          item.meta.communities = template.meta?.communities;
          item.meta.floorplan = template.meta?.floorplan;
          item.meta.elevation = template.meta?.elevation;
          item.meta.color_package = template.meta?.color_package;
          item.keywords = template.keywords;
        }
      });

      // Clear and recreate snapshots for all affected items
      applyTo.forEach(item => {
        const itemKey = item.id || item.file?.name;
        this.originalSnapshots.delete(itemKey);
        this.createSnapshot(item);
      });

      this.applied = this.selected.length > 1 ? 'selected' : 'all';
    },
    applySelection() {
      // Apply only changes (used for "Apply Updates" in edit mode)
      const applyTo = this.selected.length > 1 ? this.selected : this.attachments;
      const template = this.itemShowingInformation;

      // Compute the patch (only changed fields)
      const patch = this.computeChangedFields(template);

      // Apply patch to each target item
      applyTo.forEach(item => {
        // Don't apply to the template itself
        const itemKey = item.id || item.file?.name;
        const templateKey = template.id || template.file?.name;
        if (itemKey !== templateKey) {
          this.applyPatch(item, patch);
        }
      });

      // Clear and recreate snapshots for all affected items
      applyTo.forEach(item => {
        const itemKey = item.id || item.file?.name;
        this.originalSnapshots.delete(itemKey);
        this.createSnapshot(item);
      });

      this.applied = this.selected.length > 1 ? 'update_selected' : 'update_all';
    }
  },
  computed: {
    isEditMode() {
      // Check if at least one attachment has an ID (existing item being edited)
      return this.attachments.some(item => item.id != null);
    },
    applyToAllDisabled() {
      // If only one attachment exists, disable the button
      if (this.attachments.length <= 1) {
        return true;
      }

      // Determine which items we're applying to
      const targetItems = this.selected.length > 1 ? this.selected : this.attachments;

      // If only one item in target, disable
      if (targetItems.length <= 1) {
        return true;
      }

      return false;
    },
    applyUpdatesDisabled() {
      // If only one attachment exists, disable the button
      if (this.attachments.length <= 1) {
        return true;
      }

      // Determine which items we're applying to
      const targetItems = this.selected.length > 1 ? this.selected : this.attachments;

      // If only one item in target, disable
      if (targetItems.length <= 1) {
        return true;
      }

      const template = this.itemShowingInformation;

      // Check if there are any changes to apply
      const patch = this.computeChangedFields(template);

      // Disable if no changes detected
      if (Object.keys(patch).length === 0 || (Object.keys(patch).length === 1 && patch.meta && Object.keys(patch.meta).length === 0)) {
        return true;
      }

      return false;
    },
    unsaved() {
      return this.attachments.filter(item => item.id === null);
    },
    itemShowingInformation() {
      const save = this.selected[0] ?? new Media();
      return save;
    },
    keywordOptions() {
      const options = this.keywords ? this.keywords.map(keyword => ({ label: keyword.name, value: keyword})) : [];
      return options;
    },
    singleCommunity: {
      get() {
        return this.itemShowingInformation.meta.communities?.[0] ?? null;
      },
      set(value) {
        this.itemShowingInformation.meta.communities = value ? [value] : [];
      }
    },
    singleCollection: {
      get() {
        return this.itemShowingInformation.meta.collections?.[0] ?? null;
      },
      set(value) {
        this.itemShowingInformation.meta.collections = value ? [value] : [];
      }
    },
    collections() {
      if(this.communities.length == 0) return [];
      const retun = this.itemShowingInformation.meta.communities != null && this.itemShowingInformation.meta.communities.length > 0 ?
          this.communities.filter(community => this.itemShowingInformation.meta.communities.includes(community.id)).flatMap(community => community.collections) :
          this.communities.flatMap(community => community.collections);
      return retun;

    },
    filtered() {
      let results = this.floorplans;
      if (this.itemShowingInformation.meta.collections != null && this.itemShowingInformation.meta.collections.length > 0) {
        let collections = this.communities.filter(item => this.itemShowingInformation.meta.communities.includes(item.id)).flatMap(community => community.collections.map(collection => collection.community_rid));
        results = results.filter(item => collections.includes(item.kova.CommunityRID));
      }

      let floorplans = [
        ...new Map(
            results
                .filter(f => f.meta?.info?.name && f.kova?.ModelRID)
                .map(f => [f.kova.ModelRID, {
                  id: f.kova.ModelRID,
                  name: f.meta.info.name
                }])
        ).values()
      ];

      return floorplans;
    },
    saved() {
      return this.attachments.filter(item => item.id !== null);
    },
    size() {
      return this.attachments.reduce((prev,curr) => {
        return prev + curr.file.size
      }, 0);
    },
    validationMap() {
      return this.attachments.reduce((map, item, index) => {
        const hasCommunity = item.meta.communities != null && item.meta.communities.length > 0;
        const hasCollection = item.meta.collections != null && item.meta.collections.length > 0;

        map[index] = (item.alt != null && item.alt != '')
            && (item.caption != null && item.caption != '')
            && !this.overLimit(item)
            && (item.meta.category != null)
            && (item.meta.category == 'community' || (item.meta.category == 'home' && item.meta.position != null))
            && hasCommunity
            && (item.meta.category == 'community' || (item.meta.category == 'home' && hasCollection))
            && (item.meta.category == 'community' || (item.meta.category == 'home' && item.meta.floorplan != null));
        return map;
      }, {});
    },
    valid() {
      if (this.attachments.length == 0) return false;
      return Object.values(this.validationMap).every(isValid => isValid);
    }
  },
  watch: {
    models: {
      immediate: true,
      handler(newModels) {
        if (newModels && newModels.length > 0) {
          // Clear selected array when new models are loaded
          this.selected = [];
          // Clear snapshots for new batch
          this.originalSnapshots.clear();

          this.attachments = newModels.map((model) => {
            const media = new Media(model);
            const item = {
              ...media,
              file: model.file || null,
              keywords: media.keywords || [],
              meta: {
                ...media.meta,
                communities: media.meta.communities || [],
                collections: media.meta.collections || [],
                keywords: media.keywords || null
              }
            };
            // Create snapshot for each item
            this.createSnapshot(item);
            return item;
          });
          // include selection on the first item
          this.updateSelection(this.attachments[0]);
        }
      }
    }
  },
  created() {
    this.index();
    this.indexCommunities();
    this.indexKeywords();
  },
  props: {
    open: {
      default: false
    },
    context: {
      default: false
    },
    models: {
      type: Array,
      default: null
    }
  },
  components: {
    ModalDialog,
    FormButton,
    LoadingView,
    SelectField,
    SelectMultipleField,
    CheckboxInput,
    MultiselectField
  },

};
</script>
